import type React from 'react';
import { useEffect, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { useLocation, useParams } from 'react-router-dom';
import { useAppSelector } from 'src/app/hooks';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { TutorWindow } from 'src/features/room/components/tutor/tutor-window';
import { RemoteContentViewer } from './components/remote-content-viewer';
import { AnimatedPanelWrapper, PanelContainer, StyledPanelResizeHandle, StyledSplitScreenContent, StyledSplitScreenLeft, StyledSplitScreenMiddle, StyledSplitScreenWrapper } from './split-screen.styles';
import type { SplitScreenProps } from './split-screen.types';
export const SPLIT_SCREEN_MIDDLE_ELEMENT_ID = 'split-screen-middle';
const SplitScreen: React.FC<SplitScreenProps> = ({
  children
}) => {
  const isDarkMode = useIsDarkMode();
  const {
    roomCode
  } = useParams();
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const {
    chat: {
      displayMode
    }
  } = useAppSelector(state => state.tutor);
  const isEmbedded = displayMode === 'embedded';
  const isEmbeddedTutorOpen = tutorPanelOpen && isEmbedded;
  const [leftPanelSize, setLeftPanelSize] = useState(25);
  const [rightPanelSize, setRightPanelSize] = useState(50);
  const handleLayoutChange = (layout: number[]) => {
    if (tutorPanelOpen && vmPanelOpen) {
      if (layout[0]) setLeftPanelSize(layout[0]);
      if (layout[2]) setRightPanelSize(layout[2]);
    } else if (tutorPanelOpen) {
      if (layout[0]) setLeftPanelSize(layout[0]);
    } else if (vmPanelOpen && layout[1]) {
      setRightPanelSize(layout[1]);
    }
  };
  const isAoc2025Page = useLocation().pathname.includes('/adventofcyber25');
  return <StyledSplitScreenWrapper open={isEmbeddedTutorOpen || vmPanelOpen} data-sentry-element="StyledSplitScreenWrapper" data-sentry-component="SplitScreen" data-sentry-source-file="split-screen.tsx">
      <PanelGroup direction="horizontal" autoSaveId="split-screen-layout" onLayout={handleLayoutChange} data-sentry-element="PanelGroup" data-sentry-source-file="split-screen.tsx">
        {roomCode && <AnimatedPanel isOpen={isEmbeddedTutorOpen} position="left" defaultSize={leftPanelSize}>
            <Panel id="left-panel" order={1} minSize={20} defaultSize={leftPanelSize} maxSize={30}>
              <StyledSplitScreenLeft open={isEmbeddedTutorOpen} data-testid="splitScreenLeft">
                <StyledSplitScreenContent open={isEmbeddedTutorOpen}>
                  <TutorWindow roomCode={roomCode} />
                </StyledSplitScreenContent>
              </StyledSplitScreenLeft>
            </Panel>

            <StyledPanelResizeHandle $position="left" $isDarkMode={isDarkMode} />
          </AnimatedPanel>}

        <Panel id="middle-panel" order={2} minSize={27} defaultSize={50} data-sentry-element="Panel" data-sentry-source-file="split-screen.tsx">
          <StyledSplitScreenMiddle id={SPLIT_SCREEN_MIDDLE_ELEMENT_ID} data-testid="splitScreenMiddle" hideScrollbar={isAoc2025Page} data-sentry-element="StyledSplitScreenMiddle" data-sentry-source-file="split-screen.tsx">
            {children}
          </StyledSplitScreenMiddle>
        </Panel>

        <AnimatedPanel isOpen={vmPanelOpen} position="left" defaultSize={rightPanelSize} data-sentry-element="AnimatedPanel" data-sentry-source-file="split-screen.tsx">
          <StyledPanelResizeHandle $position="right" $isDarkMode={isDarkMode} data-sentry-element="StyledPanelResizeHandle" data-sentry-source-file="split-screen.tsx" />
          <Panel id="right-panel" order={3} minSize={20} defaultSize={50} data-sentry-element="Panel" data-sentry-source-file="split-screen.tsx">
            <RemoteContentViewer isFullscreen={false} data-sentry-element="RemoteContentViewer" data-sentry-source-file="split-screen.tsx" />
          </Panel>
        </AnimatedPanel>
      </PanelGroup>
    </StyledSplitScreenWrapper>;
};
export { SplitScreen };
const AnimatedPanel: React.FC<{
  isOpen: boolean;
  position: 'left' | 'right';
  defaultSize: number;
  children: React.ReactNode;
}> = ({
  isOpen,
  position,
  defaultSize,
  children
}) => {
  const [width, setWidth] = useState(isOpen ? defaultSize : 0);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  useEffect(() => {
    if (isOpen) {
      setWidth(defaultSize);
      const timer = setTimeout(() => setShouldAnimate(false), 500);
      return () => clearTimeout(timer);
    }
    setWidth(0);
    setShouldAnimate(true);
    return () => {};
  }, [isOpen, defaultSize]);
  return <AnimatedPanelWrapper isOpen={isOpen} position={position} width={width} shouldAnimate={shouldAnimate} data-sentry-element="AnimatedPanelWrapper" data-sentry-component="AnimatedPanel" data-sentry-source-file="split-screen.tsx">
      {isOpen && <PanelContainer isOpen={isOpen}>{children}</PanelContainer>}
    </AnimatedPanelWrapper>;
};